import { EditorV3Content, isIEditorV3 } from "../classes";

/**
 * Removes Optional (green) and Notes (blue) styles from EditorV3 content.
 *
 * Processing:
 * 1. Returns content unchanged if not IEditorV3
 * 2. Optional/OptionalLocked styles (green): converted to black text
 * 3. Notes styles (blue): text blocks removed entirely
 * 4. If a line has no textBlocks after filtering, add an empty text block
 *
 * Core utility function for all TLF style removal operations.
 *
 * Note: External singles with styles are not applied here. They should be promoted to
 * TLF-level singles before calling this function (see RemoveStylesModal).
 *
 * @template T - Content type (typically IEditorV3 or a type containing it)
 * @param content - The content to process
 * @param stylesToDelete - Array of style names for text blocks to be deleted
 * @param stylesToClean - Array of style names for text blocks to be cleaned (converted to black)
 * @returns If content is IEditorV3, returns new object with styles removed; otherwise unchanged.
 */
export const removeStylesFromEditorV3 = <T>(
  content: T,
  stylesToDelete: string[],
  stylesToClean: string[],
): T => {
  if (!isIEditorV3(content)) {
    return content;
  }

  const newContent = new EditorV3Content(content);
  newContent.deleteStyles(stylesToDelete);
  newContent.cleanStyles(stylesToClean);

  return { ...content, lines: newContent.data.lines } as T;
};
